import React, { Fragment, useState, useEffect } from "react"
import PropTypes from "prop-types"
import MediaQuery from "react-responsive"
import classNames from "classnames"
import isMobile from "components/Stacker/utils/device-check"

// Configs
import SwatchConfig from "components/Stacker/config/SwatchConfig"
import BreakPointConfig from "components/Stacker/constants/breakpoints"
import ModalConfig from "components/Stacker/config/ModalConfig"

// Actions
import { getProductStackDetails } from "components/Stacker/actions/StackItWithActions"
import { toggleModal } from "components/Stacker/actions/ModalActions"
import { updateNotifyStatus } from "components/Stacker/actions/ProductDetailActions"

// components
import Icon from "components/Stacker/components/Icon/Icon"
import ProductImageTile from "components/Stacker/components/ProductImageTile/ProductImageTile"
import ProductMetals from "components/Stacker/components/ProductMetals/ProductMetals"
import ProductSwatches from "components/Stacker/components/ProductSwatches/ProductSwatches"
import ProductSizes from "components/Stacker/components/ProductSizes/ProductSizes"
import AddToBag from "components/Stacker/components/AddToBag/AddToBag"
import Loader from "components/Stacker/components/Loader/Loader"
import LinkAdapter from "components/Stacker/components/LinkAdapter/LinkAdapter"
import { usePrevious } from "reactHooks"

// Styles
import "./StackItem.scss"
import { useDispatch } from "react-redux"
import { useI18nContext } from "i18n/i18n-react"

function StackItem(props) {
  const dispatch = useDispatch()
  const { LL } = useI18nContext()
  const [isItemExpanded, setIsItemExpanded] = useState(true)
  const [isAddedToBag, setIsAddedToBag] = useState(false)
  const {
    cartEntries,
    stackItem: {
      getPdpData,
      selectedMetal,
      data: {
        products: { productName, isGiftCard, styleNumber, productId },
      },
      selectedVariant,
      selectedSize,
      priceInventory: { price: { formattedValue, discountedPrice } = {} } = {},
      hasCartAddtionError = false,
      errorMessage,
    },
    isEPS,
    stackedCode,
    baseStore,

    cartMetaData: { isDataLoading },
  } = props

  const prevSelectedSize = usePrevious(selectedSize)
  const prevCartLength = usePrevious(cartEntries.length)
  const prevSelectedVariant = usePrevious(selectedVariant)

  useEffect(() => {
    dispatch(updateNotifyStatus(productId))
  }, [dispatch, productId])

  useEffect(() => {
    if (cartEntries.length && selectedSize) {
      handleIsAddedToBag(cartEntries, selectedSize.code)
    }
  }, [cartEntries, selectedSize])

  useEffect(() => {
    if (getPdpData) {
      const args = {
        baseStore,
        baseProductCodes: selectedMetal,
        selectedStackedCodes: [stackedCode],
      }
      dispatch(getProductStackDetails(args))
    }

    const shouldDetermineIfAddedToBag = prevCartLength !== cartEntries.length

    const isNewSizeSelected =
      selectedSize && selectedSize.code !== prevSelectedSize?.code

    const isNewVariantSelected =
      prevSelectedVariant &&
      selectedVariant &&
      selectedVariant.code !== prevSelectedVariant.code

    if (
      shouldDetermineIfAddedToBag ||
      isNewSizeSelected ||
      isNewVariantSelected
    ) {
      const sku = selectedSize ? selectedSize.code : ""
      handleIsAddedToBag(cartEntries, sku)
    }
  }, [getPdpData, selectedSize, cartEntries])

  const handleIsAddedToBag = (entries, sku) => {
    const entry = entries.filter((item) => {
      return item.product.toLowerCase() === sku.toLowerCase()
    })
    const isAddedToBag = entry.length > 0
    setIsAddedToBag(isAddedToBag)
  }

  const handleItemClick = () => {
    dispatch(toggleModal(ModalConfig.MODAL_STACK_IT_WITH, false))
  }

  const toggleState = () => {
    setIsItemExpanded(!isItemExpanded)
  }

  const renderAddToBag = () => {
    const selectedSku = isGiftCard
      ? selectedVariant && selectedVariant.code
      : selectedSize && selectedSize.code

    if (isAddedToBag) {
      const btnText = LL.generic.addedToBag()
      return (
        <div className="added-to-bag-container">
          <button
            className="c-btn secondary d-flex w-100 align-items-center justify-content-center"
            aria-label={btnText}
            disabled
            aria-disabled
          >
            {btnText}
          </button>
        </div>
      )
    }
    return (
      <AddToBag
        pid={stackedCode}
        sku={selectedSku}
        isCartDataLoading={isDataLoading}
        isError={hasCartAddtionError}
        errorMessage={errorMessage}
        isStackedProduct
      />
    )
  }

  const renderProductPrice = () => {
    return (
      <p className="product-price">
        {discountedPrice ? (
          <Fragment>
            <strike>{formattedValue}</strike>
            <span className="error-text">{discountedPrice}</span>
          </Fragment>
        ) : (
          formattedValue
        )}
      </p>
    )
  }

  if (getPdpData) {
    return <Loader />
  }

  const {
    stackItem: {
      data: {
        products: { cropQuadrant, variantOptions, isEngagementProduct, url },
        metals,
        sizeChart,
      },

      selectedVariant: {
        code,
        variantOptions: productSizes,
        productName: variantName,
        imageURL,
      },
      selectedVariantIndex,
      sizeSelectionError,
    },
    isCsAgent,
  } = props

  const showProductMetals = metals && metals.length > 1

  const isSwatchDropDown = variantOptions.length > 4

  const hideSwatches =
    variantOptions.length === SwatchConfig.SWATCH_PDP_HIDE_LIMIT

  const showSizes =
    !isEngagementProduct &&
    !isGiftCard &&
    productSizes &&
    productSizes.length > 0

  const isMobileScreen = isMobile()

  return (
    <Fragment>
      <div className="StackItem">
        <MediaQuery query={BreakPointConfig.IS_MOBILE}>
          <button
            className="p-0 text-left"
            aria-label="toggle-accordion"
            onClick={toggleState}
          >
            <div className="mobile-accordion-stack-item stack-item-row d-flex justify-content-between">
              <div className="stack-item-row__img-cont pr-0">
                <ProductImageTile
                  cropQuadrant={cropQuadrant}
                  productImage={imageURL}
                  name={variantName}
                  baseProductID={productId}
                  isDetailedView
                  swatchId={code}
                  url={url}
                  handleClick={handleItemClick}
                />
              </div>
              <div className="stack-item-row__name-cont pr-0 pl-3">
                <LinkAdapter
                  className="product-name"
                  to={url}
                  aria={{ label: variantName }}
                  baseProduct={productId}
                  customClick={handleItemClick}
                >
                  <span dangerouslySetInnerHTML={{ __html: variantName }} />
                </LinkAdapter>
                {renderProductPrice()}
              </div>
              <div className="stack-item-row__caret-cont pr-0">
                <Icon
                  additionalClassNames="icon-stack-accordion"
                  iconName={
                    isItemExpanded
                      ? "icon-arrow-up-mobile"
                      : "icon-arrow-down-mobile"
                  }
                />
              </div>
            </div>
          </button>
        </MediaQuery>
        <div
          className={classNames("stack-item-row", {
            hide: !isItemExpanded && isMobileScreen,
          })}
        >
          <MediaQuery query={BreakPointConfig.IS_DESKTOP}>
            <div className="stack-item-row__img-cont">
              <ProductImageTile
                cropQuadrant={cropQuadrant}
                productImage={imageURL}
                name={variantName}
                baseProductID={productId}
                isDetailedView
                swatchId={code}
                url={url}
                handleClick={handleItemClick}
              />
            </div>
            <div className="stack-item-row__name-cont">
              <LinkAdapter
                className="product-name"
                to={url}
                aria={{ label: variantName }}
                baseProduct={productId}
                customClick={handleItemClick}
              >
                <span dangerouslySetInnerHTML={{ __html: variantName }} />
              </LinkAdapter>
              {renderProductPrice()}
            </div>
          </MediaQuery>
          <div className="stack-item-row__swatch-cont">
            {showProductMetals && (
              <ProductMetals
                pid={stackedCode}
                productMetals={metals}
                isLink={false}
                isStackedProduct
              />
            )}

            <ProductSwatches
              pid={stackedCode}
              selectedVariantIndex={selectedVariantIndex}
              selectedVariant={selectedVariant}
              variantOptions={variantOptions}
              isSwatchDropDown={isSwatchDropDown}
              hideSwatches={hideSwatches}
              selectedSize={selectedSize}
            />

            {showSizes && (
              <ProductSizes
                productSizes={productSizes}
                pid={stackedCode}
                selectedSize={selectedSize}
                productSizeChart={sizeChart}
                isShowError={sizeSelectionError}
                isCsAgent={isCsAgent}
              />
            )}
          </div>
          <div className="stack-item-row__btn-cont">
            {/* move it to methiod and have EPS check */}
            {renderAddToBag()}
          </div>
        </div>
      </div>
    </Fragment>
  )
}

StackItem.propTypes = {
  cartMetaData: PropTypes.object,
  stackItem: PropTypes.object,
  stackedCode: PropTypes.string,
  baseStore: PropTypes.string.isRequired,
  isEPS: PropTypes.bool,
  cartEntries: PropTypes.array,
  isCsAgent: PropTypes.bool,
}

export default StackItem
